import 'swiper/css';

import {Tabs} from '@base-ui/react/tabs';
import {type ReactNode, useCallback, useRef, useState} from 'react';
import type {Swiper as SwiperInstance} from 'swiper';
import {A11y} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {twx} from '@/utils/tailwindUtils';

const DEFAULT_SPEED_MS = 400;

export type SwiperTabItem = {
    value: string;
    label?: ReactNode;
    testId?: string;
    children: ReactNode;
};

type SwiperTabsProps = {
    tabs: SwiperTabItem[];
    defaultValue?: string;
    className?: string;
    speedMs?: number;
};

const SwiperTabs = ({tabs, defaultValue, className, speedMs = DEFAULT_SPEED_MS}: SwiperTabsProps) => {
    const [activeValue, setActiveValue] = useState<string>(defaultValue ?? tabs[0]?.value ?? '');
    const [mountedIndices, setMountedIndices] = useState<ReadonlySet<number>>(new Set([0]));
    const swiperRef = useRef<SwiperInstance | null>(null);

    const onSwiper = useCallback((s: SwiperInstance) => {
        swiperRef.current = s;
    }, []);

    const handleTabChange = useCallback(
        (value: Tabs.Tab.Value) => {
            const next = value as string;
            setActiveValue(next);
            const idx = tabs.findIndex((t) => t.value === next);
            if (idx >= 0) {
                setMountedIndices((prev) => new Set([...prev, idx]));
                swiperRef.current?.slideTo(idx);
            }
        },
        [tabs]
    );

    // Keep tab state in sync if Swiper changes index (e.g. from A11y focus).
    const onSlideChange = useCallback(
        (swiper: SwiperInstance) => {
            const next = tabs[swiper.activeIndex]?.value;
            if (next && next !== activeValue) {
                setActiveValue(next);
                setMountedIndices((prev) => new Set([...prev, swiper.activeIndex]));
            }
        },
        [tabs, activeValue]
    );

    const onTransitionEnd = useCallback((swiper: SwiperInstance) => {
        setMountedIndices(new Set([swiper.activeIndex]));
    }, []);

    return (
        <Tabs.Root
            value={activeValue}
            onValueChange={handleTabChange}
            className={twx('flex min-h-0 flex-1 flex-col gap-8', className)}
        >
            <Tabs.List className='mx-4 flex shrink-0 rounded-full bg-charcoal/90 p-1'>
                {tabs.map((t) => (
                    <Tabs.Tab
                        key={t.value}
                        value={t.value}
                        data-testid={t.testId}
                        className='flex-1 cursor-pointer rounded-full border-0 bg-transparent px-4 py-2 text-14 text-romance leading-13 outline-none transition-colors duration-300 ease-in-out focus-visible:ring-2 focus-visible:ring-white/40 data-active:bg-primary-dark'
                    >
                        {t.label}
                    </Tabs.Tab>
                ))}
            </Tabs.List>

            <Swiper
                className='min-h-0 w-full flex-1'
                modules={[A11y]}
                slidesPerView={1}
                spaceBetween={16}
                allowTouchMove={false}
                speed={speedMs}
                onSwiper={onSwiper}
                onSlideChange={onSlideChange}
                onTransitionEnd={onTransitionEnd}
            >
                {tabs.map((t, i) => (
                    <SwiperSlide key={t.value}>
                        {({isActive}) => (
                            <div
                                className='flex h-full min-h-0 flex-col overflow-y-auto'
                                aria-hidden={!isActive || undefined}
                                inert={!isActive || undefined}
                            >
                                {mountedIndices.has(i) ? t.children : null}
                            </div>
                        )}
                    </SwiperSlide>
                ))}
            </Swiper>
        </Tabs.Root>
    );
};

export default SwiperTabs;
